/**
 * Appearance: deliberately NOT migrated onto role tokens.
 *
 * This is a permanently-dark surface. Video calls render camera feeds over
 * black in every product of this kind, and the chrome here is white-on-black
 * with translucent white overlays. Role tokens invert with the theme, so
 * routing these fills through `surfaceInverse` / `textOnInverse` would turn
 * the call UI WHITE in dark mode, which is the opposite of correct.
 *
 * The raw `brand.black` / `brand.white` / `rgba(255,255,255,...)` values below
 * are therefore intentional constants, in the same category as the anonymous
 * brand screens and the white glyphs on semantic fills. If the Appearance lint
 * rule is added later, this file needs an explicit exemption.
 */
import {
  LiveKitRoom,
  RoomAudioRenderer,
  useIsMuted,
  useLocalParticipant,
  useMediaDeviceSelect,
  useParticipants,
  useRemoteParticipants,
  type TrackReference,
  useTracks,
  VideoTrack,
} from '@livekit/components-react';
import type { Participant } from 'livekit-client';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import { useGrippContext } from '@gripp/shared-logic';
import { Icon, tokens, type IconName } from '@gripp/shared-ui';
import { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
  type ViewStyle,
} from 'react-native';

interface CallRoomProps {
  /** Whether to join with the camera on. */
  video?: boolean;
  url: string;
  token: string;
  /** Name of the other party, shown in the call header (direct calls). */
  peerName: string;
  /** Caller is shown a "ringing" hint until the callee joins (direct calls). */
  role: 'caller' | 'callee';
  /**
   * 'direct' = 1:1 layout (remote fills screen + local PiP, "Ringing…").
   * 'group' = N-participant grid + roster header, no ringing. Default 'direct'.
   */
  mode?: 'direct' | 'group';
  /** Title shown in the header for group calls (e.g. the Activity title). */
  title?: string;
  /** Fired when the local user leaves or the connection drops. */
  onDisconnected: () => void;
  /** Fired once a remote participant joins the room. */
  onPeerJoined?: () => void;
}

/**
 * Full-screen in-call overlay (web). Standard 1:1 layout: remote participant
 * fills the screen, the local camera sits in a tap-to-swap picture-in-picture,
 * header + control bar float over the video. Built from `@livekit/components-react`
 * primitives rather than the `<VideoConference>` prefab — the prefab's control bar
 * depends on CSS that is unreliable inside the react-native-web `<Modal>`, so a
 * hand-built bar (mic / camera / hang-up) is the dependable choice and gives
 * parity with the RN app. `<RoomAudioRenderer>` plays remote audio (the prefab
 * used to provide this). Leaving fires onDisconnected.
 */
export const CallRoom = ({
  url,
  token,
  peerName,
  role,
  mode = 'direct',
  title,
  video = true,
  onDisconnected,
  onPeerJoined,
}: CallRoomProps) => {
  const isGroup = mode === 'group';
  return (
    <Modal visible animationType="fade" onRequestClose={onDisconnected}>
      <View style={styles.container}>
        <LiveKitRoom
          serverUrl={url}
          token={token}
          connect
          video={video}
          audio
          onDisconnected={onDisconnected}
          style={{ height: '100%', width: '100%' }}
        >
          <RoomAudioRenderer />
          <PeerPresenceObserver onPeerJoined={onPeerJoined} />
          <CallStage mode={mode} peerName={peerName} role={role} />
          {isGroup ? (
            <GroupHeader title={title ?? 'Group call'} />
          ) : (
            <CallHeader peerName={peerName} role={role} />
          )}
          <ControlBar onHangUp={onDisconnected} />
        </LiveKitRoom>
      </View>
    </Modal>
  );
};

const PeerPresenceObserver = ({
  onPeerJoined,
}: {
  onPeerJoined?: () => void;
}) => {
  const hasPeer = useRemoteParticipants().length > 0;
  useEffect(() => {
    if (hasPeer) {
      onPeerJoined?.();
    }
  }, [hasPeer, onPeerJoined]);
  return null;
};

/** Display name for a participant tile — "You" for the local user, else the
 * LiveKit name (falls back to identity, which is the Gripp account id). */
const participantLabel = (p: Participant): string =>
  p.isLocal ? 'You' : p.name || p.identity;

/**
 * Chooses the video layout by LIVE participant count, not just the call `mode`:
 * any call currently holding two people (exactly one remote) gets the 1:1
 * "direct" treatment — remote fills the screen, local sits in the lower-right
 * PiP — even when it's a group call. It only expands to the grid once a third
 * person (a second remote) joins, and a group call's empty/waiting state keeps
 * the grid's "Waiting for others…" placeholder. Direct calls are always 1:1.
 */
const CallStage = ({
  mode,
  peerName,
  role,
}: {
  mode: 'direct' | 'group';
  peerName: string;
  role: 'caller' | 'callee';
}) => {
  const remoteCount = useRemoteParticipants().length;
  if (remoteCount === 1 || mode !== 'group') {
    return <Stage peerName={peerName} role={role} />;
  }
  return <GridStage />;
};

/**
 * Group-call header: the Activity title + a live roster count. No "Ringing…"
 * (group calls are join-based, not rung).
 */
const GroupHeader = ({ title }: { title: string }) => {
  const count = useParticipants().length;
  return (
    <View style={styles.header} pointerEvents="none">
      <Text style={styles.peerName} numberOfLines={1}>
        {title}
      </Text>
      <Text style={styles.subtitle}>
        {count} {count === 1 ? 'person' : 'people'} in call
      </Text>
    </View>
  );
};

/** The sources a stage subscribes to: camera plus (web-only) screen share. */
const STAGE_SOURCES = [Track.Source.Camera, Track.Source.ScreenShare];

/**
 * Picks the ONE video track to show for a participant: their screen share while
 * it is live, else their camera. Screen share replaces the camera tile rather
 * than adding a second one — a focus/presenter layout is a bigger design
 * decision than this call UI warrants, and the PiP + grid already assume one
 * tile per participant. A share that is published but muted falls back to the
 * camera so the tile never goes black just because a share is paused.
 */
const pickVideoTrack = (
  tracks: TrackReference[],
  matches: (p: Participant) => boolean
): TrackReference | undefined => {
  const mine = tracks.filter((t) => matches(t.participant));
  const share = mine.find(
    (t) => t.source === Track.Source.ScreenShare && !t.publication.isMuted
  );
  return share ?? mine.find((t) => t.source === Track.Source.Camera);
};

const isScreenShare = (t: TrackReference): boolean =>
  t.source === Track.Source.ScreenShare;

/**
 * Group video stage: an even grid of participant tiles (local + remote), one per
 * participant. Shows each participant's camera, or a black tile with their name
 * when their camera is off / not yet published. Audio for everyone is handled by
 * <RoomAudioRenderer> at the room level.
 */
const GridStage = () => {
  const remotes = useRemoteParticipants();
  const tracks = useTracks(STAGE_SOURCES, { onlySubscribed: false });

  const localTrack = pickVideoTrack(tracks, (p) => p.isLocal);

  // The grid sizes to the REMOTE participants only; the local user sits in a
  // lower-right picture-in-picture (matching the 1:1 layout) rather than taking
  // an equal grid tile. Before anyone else joins, show a "waiting" placeholder
  // behind the self-PiP instead of a lone full-screen self tile.
  const n = Math.max(remotes.length, 1);
  const cols = n <= 1 ? 1 : n <= 4 ? 2 : 3;
  const rows = Math.ceil(n / cols);
  const tileSize: ViewStyle = {
    width: `${100 / cols}%` as unknown as ViewStyle['width'],
    height: `${100 / rows}%` as unknown as ViewStyle['height'],
  };

  return (
    <View style={styles.stage}>
      {remotes.length === 0 ? (
        <View style={styles.avatarWrap} pointerEvents="none">
          <Text style={styles.avatarName}>Waiting for others to join…</Text>
        </View>
      ) : (
        <View style={styles.grid}>
          {remotes.map((p) => {
            const trackRef = pickVideoTrack(
              tracks,
              (tp) => tp.identity === p.identity
            );
            return (
              <View key={p.sid} style={[styles.gridTile, tileSize]}>
                {trackRef ? (
                  <FillVideo trackRef={trackRef} mirror={false} />
                ) : (
                  <View style={styles.cameraOff} />
                )}
                <View style={styles.tileLabel} pointerEvents="none">
                  <Text style={styles.tileLabelText} numberOfLines={1}>
                    {participantLabel(p)}
                  </Text>
                </View>
              </View>
            );
          })}
        </View>
      )}
      <View style={styles.pip}>
        {localTrack ? (
          <FillVideo trackRef={localTrack} mirror />
        ) : (
          <View style={styles.cameraOff} />
        )}
        <View style={styles.tileLabel} pointerEvents="none">
          <Text style={styles.tileLabelText} numberOfLines={1}>
            You
          </Text>
        </View>
      </View>
    </View>
  );
};

/**
 * Peer name + a "Ringing…" hint shown to the caller ONLY until a remote
 * participant joins. Presence-driven (`useRemoteParticipants`) rather than
 * call-state-driven, so it clears the moment the callee answers even if the
 * caller's `updatedCall` event is delayed. `pointerEvents="none"` so it never
 * swallows clicks meant for the video.
 */
const CallHeader = ({
  peerName,
  role,
}: {
  peerName: string;
  role: 'caller' | 'callee';
}) => {
  const remoteParticipants = useRemoteParticipants();
  const ringing = role === 'caller' && remoteParticipants.length === 0;

  return (
    <View style={styles.header} pointerEvents="none">
      <Text style={styles.peerName} numberOfLines={1}>
        {peerName}
      </Text>
      {ringing && <Text style={styles.subtitle}>Ringing…</Text>}
    </View>
  );
};

/**
 * Two-letter initials for the fallback avatar, derived from the peer's display
 * name (first letter of the first two words, else the first two letters).
 */
const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
};

/**
 * Centered call avatar shown on the stage while there is no remote video — an
 * audio call or the pre-answer ringing state. A large initials disc with the
 * peer name and a status line. The floating top header still owns the compact
 * name chip; this is the focal point of the call screen.
 */
const CallAvatar = ({
  peerName,
  status,
}: {
  peerName: string;
  status?: string;
}) => (
  <View style={styles.avatarWrap} pointerEvents="none">
    <View style={styles.avatarDisc}>
      <Text style={styles.avatarInitials}>{initialsOf(peerName)}</Text>
    </View>
    <Text style={styles.avatarName} numberOfLines={1}>
      {peerName}
    </Text>
    {status ? <Text style={styles.avatarStatus}>{status}</Text> : null}
  </View>
);

/**
 * Fills its parent with a participant's camera; mirrors local (self-view).
 *
 * A screen share is letterboxed (`contain`) instead of cropped: a cropped
 * camera loses nothing anyone cares about, a cropped screen loses the toolbar
 * or the sidebar someone is pointing at. Shares are also never mirrored — the
 * mirror exists so your own face moves the way a mirror does, and it would
 * render shared text backwards.
 *
 * When the camera is off (track muted) the underlying `<video>` element freezes
 * on its last decoded frame rather than clearing, so a disabled self-view would
 * otherwise show a frozen still. `useIsMuted` lets us render a plain black fill
 * instead — for the local self-view when you turn your camera off, and for the
 * remote tile when the other party turns theirs off.
 */
const FillVideo = ({
  trackRef,
  mirror,
}: {
  trackRef: NonNullable<Parameters<typeof VideoTrack>[0]['trackRef']>;
  mirror: boolean;
}) => {
  const isMuted = useIsMuted(trackRef);
  const share = isScreenShare(trackRef);

  if (isMuted) {
    return <View style={styles.cameraOff} />;
  }

  return (
    <VideoTrack
      trackRef={trackRef}
      style={{
        width: '100%',
        height: '100%',
        objectFit: share ? 'contain' : 'cover',
        transform: mirror && !share ? 'scaleX(-1)' : undefined,
      }}
    />
  );
};

/**
 * Video stage: remote camera full-screen with the local camera in a tap-to-swap
 * PiP. Before the remote joins, the local camera fills the screen and no PiP is
 * shown. (Web has no SurfaceView z-order issue — DOM stacking via zIndex.)
 */
const Stage = ({
  peerName,
  role,
}: {
  peerName: string;
  role: 'caller' | 'callee';
}) => {
  const tracks = useTracks(STAGE_SOURCES, { onlySubscribed: false });
  const remoteParticipants = useRemoteParticipants();
  const [swapped, setSwapped] = useState(false);

  const localTrack = pickVideoTrack(tracks, (p) => p.isLocal);
  const remoteTrack = pickVideoTrack(tracks, (p) => !p.isLocal);

  if (!remoteTrack) {
    // Status reflects remote PARTICIPANT presence, not remote video. A remote
    // who is in the room but publishes no camera — a phone caller bridged over
    // SIP (audio-only), or anyone with their camera off — is connected, not
    // "Connecting…". Mirrors CallHeader's presence-driven approach. Only show
    // ringing/connecting before the remote actually joins.
    const status =
      remoteParticipants.length > 0
        ? undefined
        : role === 'caller'
        ? 'Ringing…'
        : 'Connecting…';
    return (
      <View style={styles.stage}>
        <CallAvatar peerName={peerName} status={status} />
        {localTrack && (
          <View style={styles.pip}>
            <FillVideo trackRef={localTrack} mirror />
          </View>
        )}
      </View>
    );
  }

  const bigTrack = swapped ? localTrack : remoteTrack;
  const pipTrack = swapped ? remoteTrack : localTrack;

  return (
    <View style={styles.stage}>
      {bigTrack && (
        <FillVideo trackRef={bigTrack} mirror={bigTrack.participant.isLocal} />
      )}
      {pipTrack && (
        <Pressable
          style={styles.pip}
          onPress={() => setSwapped((s) => !s)}
          accessibilityRole="button"
          accessibilityLabel="Swap video"
        >
          <FillVideo
            trackRef={pipTrack}
            mirror={pipTrack.participant.isLocal}
          />
        </Pressable>
      )}
    </View>
  );
};

interface ControlBarProps {
  onHangUp: () => void;
}

/**
 * In-call controls: toggle mic, toggle camera, share screen (web only), hang up.
 */
const ControlBar = ({ onHangUp }: ControlBarProps) => {
  const {
    localParticipant,
    isMicrophoneEnabled,
    isCameraEnabled,
    isScreenShareEnabled,
  } = useLocalParticipant();
  const { logger } = useGrippContext();
  // Screen capture is a desktop-browser feature; iOS Safari has no
  // getDisplayMedia, so the control simply isn't offered there. Ending the share
  // from the browser's own "Stop sharing" bar unpublishes the track, and
  // `isScreenShareEnabled` follows — no extra handling needed.
  const canShareScreen =
    typeof navigator !== 'undefined' &&
    typeof navigator.mediaDevices?.getDisplayMedia === 'function';

  const toggleMic = () => {
    localParticipant
      .setMicrophoneEnabled(!isMicrophoneEnabled)
      .catch((err) => logger?.error(err));
  };
  const toggleCamera = () => {
    localParticipant
      .setCameraEnabled(!isCameraEnabled)
      .catch((err) => logger?.error(err));
  };
  const toggleScreenShare = () => {
    localParticipant
      .setScreenShareEnabled(!isScreenShareEnabled)
      .catch((err: unknown) => {
        // Dismissing the browser's picker rejects with NotAllowedError
        // (Chromium/Safari) or AbortError (Firefox); that is the user changing
        // their mind, not a failure worth a Sentry event.
        if (
          err instanceof DOMException &&
          (err.name === 'NotAllowedError' || err.name === 'AbortError')
        ) {
          return;
        }
        logger?.error(err);
      });
  };

  // Layout mirrors the Figma "Cal Bar/Call Out": red hang-up disc on the left, a
  // neutral pill holding the camera controls in the middle, and the mic toggle
  // as a neutral disc on the right.
  return (
    <View style={styles.controlBar}>
      <View style={styles.controlRow}>
        <RoundControl
          iconName="CallEnd"
          label="Hang up"
          variant="danger"
          onPress={onHangUp}
        />
        <View style={styles.pillBar}>
          <PillControl
            iconName="VideoCall"
            label={isCameraEnabled ? 'Turn camera off' : 'Turn camera on'}
            active={isCameraEnabled}
            onPress={toggleCamera}
          />
          {canShareScreen && (
            <PillControl
              iconName="Share"
              label={isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}
              active={isScreenShareEnabled}
              onPress={toggleScreenShare}
            />
          )}
          <CameraSwitcher />
        </View>
        <RoundControl
          iconName={isMicrophoneEnabled ? 'Microphone' : 'MicrophoneMuted'}
          label={isMicrophoneEnabled ? 'Mute microphone' : 'Unmute microphone'}
          onPress={toggleMic}
        />
      </View>
    </View>
  );
};

/**
 * Desktop camera picker (Slack-style): a control button that opens a small
 * popover listing the available video inputs, with the active one highlighted.
 * Rendered only when there is more than one camera to choose from, so a laptop
 * with a single webcam shows nothing. `useMediaDeviceSelect` handles enumeration
 * + switching the live LiveKit publication to the chosen device.
 */
const CameraSwitcher = () => {
  const { devices, activeDeviceId, setActiveMediaDevice } = useMediaDeviceSelect(
    { kind: 'videoinput' }
  );
  const { logger } = useGrippContext();
  const [open, setOpen] = useState(false);

  if (devices.length <= 1) {
    return null;
  }

  const selectDevice = (deviceId: string) => {
    setOpen(false);
    setActiveMediaDevice(deviceId).catch((err) => logger?.error(err));
  };

  return (
    <View style={styles.switcherWrap}>
      {open && (
        <>
          {/* Full-screen catcher so a click anywhere else dismisses the menu. */}
          <Pressable
            style={styles.switcherBackdrop}
            onPress={() => setOpen(false)}
            accessibilityRole="button"
            accessibilityLabel="Close camera picker"
          />
          <View style={styles.deviceMenu}>
            {devices.map((device, index) => {
              const isActive = device.deviceId === activeDeviceId;
              const label = device.label || `Camera ${index + 1}`;
              return (
                <Pressable
                  key={device.deviceId}
                  onPress={() => selectDevice(device.deviceId)}
                  accessibilityRole="button"
                  accessibilityLabel={`Use ${label}`}
                  accessibilityState={{ selected: isActive }}
                  style={styles.deviceItem}
                >
                  <Text
                    style={[
                      styles.deviceLabel,
                      isActive && styles.deviceLabelActive,
                    ]}
                    numberOfLines={1}
                  >
                    {label}
                  </Text>
                </Pressable>
              );
            })}
          </View>
        </>
      )}
      <PillControl
        iconName="Reset"
        label="Switch camera"
        onPress={() => setOpen((o) => !o)}
      />
    </View>
  );
};

interface RoundControlProps {
  iconName: IconName;
  label: string;
  variant?: 'neutral' | 'danger';
  onPress: () => void;
}

/**
 * A 56px circular control (hang-up / mic) from the Figma bar. Local to this file
 * — there is no Crop Circle round icon-button primitive and the control bar is
 * the only consumer. Danger = the red hang-up disc.
 */
const RoundControl = ({
  iconName,
  label,
  variant = 'neutral',
  onPress,
}: RoundControlProps) => (
  <Pressable
    onPress={onPress}
    accessibilityRole="button"
    accessibilityLabel={label}
    style={[
      styles.roundControl,
      variant === 'danger' ? styles.roundDanger : styles.roundNeutral,
    ]}
  >
    <Icon name={iconName} size={24} color={tokens.colors.brand.white} />
  </Pressable>
);

interface PillControlProps {
  iconName: IconName;
  label: string;
  /** When false the control reads as "off" (dimmed) — e.g. camera off. */
  active?: boolean;
  onPress: () => void;
}

/** A flex control inside the neutral middle pill (camera toggle / switch). */
const PillControl = ({
  iconName,
  label,
  active = true,
  onPress,
}: PillControlProps) => {
  return (
    <Pressable
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={label}
      style={[styles.pillControl, { opacity: active ? 1 : 0.5 }]}
    >
      <Icon name={iconName} size={24} color={tokens.colors.brand.white} />
    </Pressable>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: tokens.colors.brand.black,
  },
  stage: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tokens.colors.brand.black,
  },
  // Group-call tile grid: fills the screen, tiles flow left-to-right, top-to-bottom.
  grid: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: tokens.colors.brand.black,
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  gridTile: {
    borderWidth: 1,
    borderColor: tokens.colors.brand.black,
    backgroundColor: tokens.colors.neutral[800],
    overflow: 'hidden',
  },
  tileLabel: {
    position: 'absolute',
    left: tokens.spacing.sm,
    bottom: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.sm,
    paddingVertical: 2,
    borderRadius: tokens.borderRadius.sm,
    backgroundColor: 'rgba(0, 0, 0, 0.55)',
  },
  tileLabelText: {
    ...tokens.typography.label.medium,
    color: tokens.colors.brand.white,
  },
  // Shown in place of a camera tile when that camera is off — a plain black fill
  // instead of the frozen last frame the paused <video> would otherwise show.
  cameraOff: {
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colors.brand.black,
  },
  header: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    flexDirection: 'row',
    alignItems: 'baseline',
    gap: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.lg,
    paddingVertical: tokens.spacing.md,
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  peerName: {
    ...tokens.typography.title.medium,
    color: tokens.colors.brand.white,
  },
  subtitle: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  pip: {
    position: 'absolute',
    right: tokens.spacing.lg,
    bottom: 110,
    zIndex: 20,
    width: 160,
    height: 220,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: 'rgba(255, 255, 255, 0.8)',
    backgroundColor: tokens.colors.brand.black,
    overflow: 'hidden',
  },
  // Centered avatar shown on the stage while there is no remote video (audio
  // call / ringing). An initials disc + name + status line.
  avatarWrap: {
    ...StyleSheet.absoluteFillObject,
    alignItems: 'center',
    justifyContent: 'center',
    gap: tokens.spacing.md,
    paddingHorizontal: tokens.spacing.xl,
  },
  avatarDisc: {
    width: 112,
    height: 112,
    borderRadius: 56,
    backgroundColor: tokens.colors.neutral[700],
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarInitials: {
    color: tokens.colors.brand.white,
    fontSize: 40,
    lineHeight: 48,
    fontWeight: '600',
  },
  avatarName: {
    ...tokens.typography.title.medium,
    color: tokens.colors.brand.white,
    textAlign: 'center',
  },
  avatarStatus: {
    ...tokens.typography.label.medium,
    color: tokens.colors.neutral[400],
  },
  controlBar: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 10,
    alignItems: 'center',
    paddingTop: tokens.spacing.lg,
    paddingBottom: tokens.spacing['2xl'],
    backgroundColor: 'rgba(0, 0, 0, 0.35)',
  },
  // Constrains the pill group to a phone-like width and centers it on desktop.
  controlRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    width: '100%',
    maxWidth: 420,
    paddingHorizontal: tokens.spacing.lg,
  },
  // 56px round discs: red for hang-up, neutral for the mic toggle.
  roundControl: {
    width: 56,
    height: 56,
    borderRadius: 28,
    alignItems: 'center',
    justifyContent: 'center',
  },
  roundNeutral: {
    backgroundColor: tokens.colors.neutral[800],
  },
  roundDanger: {
    backgroundColor: tokens.colors.semantic.error,
  },
  // Neutral middle pill holding the camera controls.
  pillBar: {
    flex: 1,
    height: 56,
    borderRadius: 28,
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: tokens.spacing.md,
    backgroundColor: tokens.colors.neutral[800],
  },
  pillControl: {
    flex: 1,
    height: 40,
    borderRadius: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  // Anchor for the camera-picker popover so the menu floats directly above the
  // switch-camera button. Fills its half of the pill.
  switcherWrap: {
    flex: 1,
    position: 'relative',
  },
  // Invisible full-screen catcher: a click outside the open menu closes it.
  // RN-Web honors position:'fixed' (viewport-anchored, so it covers the whole
  // call overlay rather than just the tiny switcher wrapper), but RN's style
  // types stop at 'absolute' — cast to keep the viewport-anchored behaviour.
  switcherBackdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: 25,
  } as unknown as ViewStyle,
  deviceMenu: {
    position: 'absolute',
    bottom: 56 + tokens.spacing.sm,
    left: '50%',
    transform: [{ translateX: -120 }],
    width: 240,
    backgroundColor: tokens.colors.neutral[800],
    borderRadius: tokens.borderRadius.md,
    paddingVertical: tokens.spacing.xs,
    zIndex: 30,
  },
  deviceItem: {
    paddingVertical: tokens.spacing.sm,
    paddingHorizontal: tokens.spacing.md,
  },
  deviceLabel: {
    ...tokens.typography.body.small,
    color: tokens.colors.neutral[300],
  },
  deviceLabelActive: {
    color: tokens.colors.brand.white,
    ...tokens.typography.body.smallEmphasised,
  },
});
